import { defineStore } from "pinia";
import AssetService from "@/services/AssetService";
import HotSpotService from "@/services/HotSpotService";
import InspectionAreaService from "@/services/InspectionAreaService";

// Single source of truth for the marker data shown in the asset viewer (anomalies,
// hotspots, inspection zones, camera helpers). Owns the array, the load actions, and
// the add/remove/update primitives that ViewAsset used to hold directly.
//
// Note: marker objects carry a markRaw'd three.js `meshGroup` attached by ModelViewer.
// Replacing a marker object would drop that mesh, so `update` replaces by id with the
// caller's object (callers mutate the existing object's fields in place, e.g. eTag).
export const useMarkerStore = defineStore("markers", {
  state: () => ({
    markers: [],
  }),
  getters: {
    byId: (state) => (id) => state.markers.find((m) => m.id === id),
  },
  actions: {
    // These reassign `markers` (new array reference) rather than mutating in place. The
    // array flows ViewAsset.markers(computed) → ModelViewer :markers; a new reference
    // reliably re-triggers the viewer's markers watcher (an in-place push/splice through
    // the computed does not), so add/remove/update show up in the 3D scene immediately.
    add(marker) {
      this.markers = [...this.markers, marker];
    },
    remove(id) {
      this.markers = this.markers.filter((m) => m.id !== id);
    },
    update(marker) {
      this.markers = this.markers.map((m) => (m.id === marker.id ? marker : m));
    },
    clear() {
      this.markers = [];
    },

    async loadAnomalyMarkers(scanId) {
      const anomalyMarkers = await AssetService.getAnomalyMarkersByScanId(scanId);
      const active = anomalyMarkers.filter((m) => m.isActive == true);
      this.markers = [...this.markers, ...active];
    },
    async loadHotSpotMarkers(scanId) {
      const hotSpotMarkers = await HotSpotService.getHotSpotMarkersByScanId(scanId);
      this.markers = [...this.markers, ...hotSpotMarkers];
    },
    async loadInspectionAreaMarkers(scanId) {
      const inspectionAreas = await InspectionAreaService.getInspectionAreaMarkersByScanId(scanId);
      this.markers = [...this.markers, ...inspectionAreas];
    },
    async loadCameraHelpers(scanId) {
      const cameraHelpers = await AssetService.getCameraHelpersAsync(scanId);
      this.markers = [...this.markers, ...cameraHelpers];
    },
  },
});
